import { Checkbox, FormControl, FormControlLabel, FormHelperText, Stack, styled, TextField, type TextFieldProps, Typography } from "@mui/material";
import { useState } from "react";
import { useApiContext } from "../../api/ApiProvider";
import { ColorHexCodes } from "../../util/Color";
import useEventId from "../../util/useEventId";
import type { ReservationState } from "../../util/useReservationState";
import { validateMail, validateName, validatePhone } from "../../util/validater";
import { FloatingUI } from "../FloatingUI";
import FormLabel from "../FormLabel";
import { FormWrapper } from "../FormWrapper";
import Space from "../Space";

const TextFieldWithFieldColor = styled((props: TextFieldProps) => <TextField {...props} />)(({ theme, error }) => ({
  "& .MuiFormHelperText-root": {
    margin: 0,
  },
  "& .MuiInputBase-input": {
    height: "1rem",
  },
  "& .MuiInputBase-root": {
    backgroundColor: error ? `#${ColorHexCodes.ErrorColor}` : "transparent",
  },
}));

export default function Form2({ state }: { state: ReservationState }) {
  const { api } = useApiContext();
  const eventId = useEventId();
  const isNameValid = validateName(state.data.name);
  const isPhoneValid = validatePhone(state.data.phone);
  const isMailValid = validateMail(state.data.mail);
  const cancelPolicyEnabled = state.data.store!.cancel_policy !== null;
  const [cancelPolicyAccepted, setCancelPolicyAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const isValid = isNameValid && isPhoneValid && isMailValid && (!cancelPolicyEnabled || cancelPolicyAccepted) && privacyPolicyAccepted;

  const searchParams = new URLSearchParams(window.location.search);
  const canGoBack = searchParams.get("shop") === null || searchParams.get("course") === null || searchParams.get("startAt") === null;

  const onPrev = () => {
    state.setValue("date", undefined);
  };

  const onNext = () => {
    if (state.data.store?.skip_confirm) {
      if (state.data.posted) {
        return;
      }
      state.setValue("posted", true);
      api.PostReserve(state.data, eventId, searchParams).then((response) => {
        if (response) {
          state.setValue("rid", response.rid);
          state.setValue("lineLoginUrl", response.line_login_url);
          state.setValue("accepted", true);
        } else {
          state.setValue("error", true);
        }
      });
    } else {
      state.setValue("confirmed", true);
    }
  };

  return (
    <FormWrapper currentIndex={1} skipConfirm={state.data.store?.skip_confirm}>
      <Stack style={{ gap: 20 }}>
        <FormItem error={!isNameValid} onChange={(v) => state.setValue("name", v)} placeholder="山田花子" required text="お名前" value={state.data.name} />
        <FormItem error={!isPhoneValid} onChange={(v) => state.setValue("phone", v)} placeholder="08012345678" required text="電話番号" value={state.data.phone} />
        <FormItem error={!isMailValid} onChange={(v) => state.setValue("mail", v)} placeholder="info@example.com" required text="メールアドレス" value={state.data.mail} />
        <FormItem multiline onChange={(v) => state.setValue("request", v)} optional placeholder={state.data.store?.reservation_request_placeholder ?? ""} text="ご要望" value={state.data.request} />
        {cancelPolicyEnabled && <CancelPolicy accepted={cancelPolicyAccepted} cancelPolicy={state.data.store!.cancel_policy} setAccepted={setCancelPolicyAccepted} />}
        <PrivacyPolicy accepted={privacyPolicyAccepted} setAccepted={setPrivacyPolicyAccepted} />
      </Stack>
      <Space h={160} />
      <FloatingUI canGoBack={canGoBack} isValid={isValid} onNext={onNext} onPrev={onPrev} state={state} />
    </FormWrapper>
  );
}

function FormItem({ text, placeholder, value, onChange, multiline, required, optional, error }: { text: string; placeholder: string; value: string; onChange: (value: string) => void; multiline?: boolean; required?: boolean; optional?: boolean; error?: boolean }) {
  const minRows = multiline ? 4 : 1;
  return (
    <Stack style={{ gap: 10 }}>
      <FormLabel optional={optional} required={required} text={text} />
      <TextFieldWithFieldColor error={error} helperText={text + "を入力してください"} minRows={minRows} multiline={multiline} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} value={value} />
    </Stack>
  );
}

function CancelPolicy({ cancelPolicy, accepted, setAccepted }: { cancelPolicy: string | null; accepted: boolean; setAccepted: (accepted: boolean) => void }) {
  if (cancelPolicy === null) {
    return <></>;
  }
  return (
    <Stack style={{ gap: 10 }}>
      <FormLabel required={true} text={"キャンセルポリシー"} />
      <Typography fontSize=".8rem" fontWeight="bold">
        {cancelPolicy}
      </Typography>
      <FormControl component="fieldset" error={!accepted} required sx={{ m: 0 }} variant="standard">
        <Stack alignItems="center" direction="row">
          <FormControlLabel control={<Checkbox onChange={(e) => setAccepted(e.target.checked)} value={accepted} />} label="キャンセルポリシーに同意する" required />
        </Stack>
        <FormHelperText>キャンセルポリシーを確認の上チェックをお願いします</FormHelperText>
      </FormControl>
    </Stack>
  );
}

function PrivacyPolicy({ accepted, setAccepted }: { accepted: boolean; setAccepted: (accepted: boolean) => void }) {
  return (
    <Stack style={{ gap: 10 }}>
      <FormLabel required={true} text={"プライバシーポリシー"} />

      <FormControl component="fieldset" error={!accepted} required sx={{ m: 0 }} variant="standard">
        <Stack alignItems="center" direction="row">
          <FormControlLabel
            control={<Checkbox onChange={(e) => setAccepted(e.target.checked)} value={accepted} />}
            label={
              <>
                <a href="https://quick-reserve.com/privacy.html" rel="noopener" target="_blank">
                  プライバシーポリシー
                </a>
                に同意する
              </>
            }
            required
          />
        </Stack>

        <FormHelperText>プライバシーポリシーを確認の上チェックをお願いします</FormHelperText>
      </FormControl>
    </Stack>
  );
}
